import { useState } from "react";
import { Accordion, AccordionItem } from "../components/ui/accordion";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle, Menu, X, Check, ChevronUp, ChevronDown, Search } from "lucide-react";
import { IoVideocamOutline } from "react-icons/io5";
import { FaFileAlt } from "react-icons/fa";
import { FaArrowRightLong } from "react-icons/fa6";



export default function CourseDashboard() {
  const location = useLocation();
  const [selectedTime, setSelectedTime] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Initially closed on mobile
  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // State for search query

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const courseChapters = [
    {
      title: "Course Overview",
      completion: "0 / 1",
      lessons: [
        { title: "Here's what you'll gain", time: "2 min", link: "/courses/take/ai-tools-at-work-certification/lessons/59788076-here-s-what-you-ll-gain" }
      ]
    },
    {
      title: "What Is AI ❓",
      completion: "0 / 5",
      lessons: [
        { title: "AI Tools you need to know", time: "3 min", link: "/courses/take/ai-tools-at-work-certification/lessons/59783831-ai-tools-you-need-to-know" },
        { title: "What AI is", time: "4 min", link: "/courses/take/ai-tools-at-work-certification/lessons/59787416-what-ai-is" },
        { title: "AI's impact & types - why it matters for you", time: "5 min", link: "/courses/take/ai-tools-at-work-certification/lessons/59787420-ai-s-impact-types-why-it-matters-for-you" },
        { title: "How to use AI in your daily life", time: "6 min", link: "/courses/take/ai-tools-at-work-certification/lessons/59787421-how-to-use-ai-in-your-daily-life" },
        { title: "Use AI to save time and live better", time: "7 min", link: "/courses/take/ai-tools-at-work-certification/lessons/59787424-use-ai-to-save-time-and-live-better" }
      ]
    }
  ];

  // Filter chapters and lessons based on search query
  const filteredChapters = courseChapters.map((chapter) => ({
    ...chapter,
    lessons: chapter.lessons.filter((lesson) =>
      lesson.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter((chapter) => chapter.lessons.length > 0); // Only show chapters with matching lessons

  return (
    <div className="h-screen flex flex-col">
      <header className="bg-background-back2 text-white p-4 flex justify-between items-center shadow-md flex-shrink-0 z-40">
        <h1 className="text-lg font-bold hidden md:block">My Dashboard</h1>
        <button onClick={toggleSidebar} className="text-white p-2 block md:hidden ">
          {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <div className="text-xl font-semibold">
          <Link to="/" className="custom-logo-link" rel="home">
            <img src="/images/NFA-Logo.png" alt="NFA Logo" className="h-8" />
          </Link>
        </div>
      </header>

      <div className="flex flex-1 bg-background-back1 overflow-hidden relative">
        {/* Overlay for Mobile Sidebar */}
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-20 md:hidden"
            onClick={closeSidebar}
          ></div>
        )}

        {/* Sidebar */}
        <aside
  className={`fixed md:relative w-74 md:w-1/4 h-full p-4 border-r border-white/20 shadow-lg bg-background-back1 z-30 transform transition-transform duration-300 ease-in-out overflow-y-auto ${
    isSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
  }`}
>
  <section className="mb-5">
    <div className="p-4 shadow-md rounded-lg mt-2 bg-background-back2 text-white">
      <h1 className="text-2xl font-bold">AI Tools at Work Course</h1>
      <div className="mt-4">
        <div className="w-full bg-gray-300 h-2 rounded-full overflow-hidden">
          <div className="bg-blue-600 h-2 w-1/6"></div>
        </div>
        <p className="mt-2 text-gray-400 text-sm"><span>10%</span> complete</p>
      </div>
    </div>
  </section>

  {/* Search Bar */}
  <div className="mb-4">
    <div className="relative">
      <input
        type="text"
        placeholder="Search by lesson title"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-background-back2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
    </div>
  </div>

  <h2 className="font-bold text-lg mb-3 text-white">Course Chapters</h2>
  <Accordion>
    {filteredChapters.map((chapter, index) => {
      const isOpen = openIndex === index; // Check if this accordion is open

      return (
        <AccordionItem
          key={index}
          title={
            <div
              className="flex items-center justify-between p-2 text-white rounded-md shadow-sm cursor-pointer"
              onClick={() => setOpenIndex(isOpen ? null : index)} // Toggle open state
            >
              <div className="flex items-center gap-2">
                {chapter.completion.split(" / ")[0] === chapter.completion.split(" / ")[1] ? (
                  <CheckCircle className="text-green-500 w-6 h-6" /> // Completed chapter
                ) : (
                  <div className="w-6 h-6 border border-white rounded-full"></div> // Empty circle
                )}
                <span className="font-medium"><span>{chapter.title}</span> </span>
              </div>
              <span className="flex">
                <span className="font-normal">1 / 1</span>
                {/* Up/Down Arrow */}
                {isOpen ? (
                  <ChevronUp className="text-white w-5 h-5" />
                ) : (
                  <ChevronDown className="text-white w-5 h-5" />
                )}
              </span>
            </div>
          }
        >
          <ul className="space-y-3 ml-1">
            {chapter.lessons.map((lesson, i) => {
              const isActive = location.pathname === lesson.link;
              return (
                <li
                  key={i}
                  className="flex items-start gap-3 p-2 rounded-lg transition-all relative group" // Added `group` for conditional styling
                >
                  {/* Vertical line - Conditionally rendered */}
                  {i !== chapter.lessons.length - 0 && ( // Only show the line if it's not the last item
                    <div className="absolute left-[14px] top-[32px] bottom-[-20px] w-[2px] bg-gray-400"></div>
                  )}

                  {/* Checkbox (circle with checkmark) */}
                  <div className="w-4 h-4 flex items-center justify-center border border-white rounded-full mt-1 relative z-10 bg-background-back1">
                    {isActive && <Check className="text-green-400 w-3 h-3" />}
                  </div>

                  {/* Lesson title and details */}
                  <Link
                    to={lesson.link}
                    onClick={closeSidebar}
                    className="text-white hover:text-blue-600 text-sm font-semibold flex-1"
                  >
                    {lesson.title}{" "}
                    <span className="text-gray-500 text-xs flex items-center gap-1 uppercase">
                      <IoVideocamOutline className="w-4 h-4" /> Video {/* Video icon */}
                      {lesson.time} {/* Duration */}
                    </span>
                  </Link>
                </li>
              );
            })}

            {/* Additional Text-Based List Item */}
            <li className="flex items-start gap-3 p-2 rounded-lg transition-all relative">
              {/* Vertical line - Conditionally rendered */}
              

              {/* Checkbox (circle with checkmark) */}
              <div className="w-4 h-4 flex items-center justify-center border border-white rounded-full mt-1 relative z-10 bg-background-back1">
                <Check className="text-green-400 w-3 h-3" /> {/* Assuming this is completed */}
              </div>

              {/* Text-Based Item */}
              <div className="text-white text-sm font-semibold flex-1">
                <div className="content-item__title">
                  Additional resources and tutorials
                  <div className="content-item__details text-gray-500 text-xs flex items-center gap-1 uppercase">
                    <FaFileAlt className="w-4 h-4" /> {/* React Icons text icon */}
                    Text
                  </div>
                </div>
              </div>
            </li>
          </ul>
        </AccordionItem>
      );
    })}
  </Accordion>
</aside>

        {/* Main Content */}
        <main className="flex-1  bg-background-back1 overflow-y-auto relative">
          <div className="p-2 pl-6 bg-background-back2 text-center sticky top-0 w-full border-t border-white/20 z-[9]">
            <h2 className="font-bold text-left text-lg  text-white">Here's what you'll gain</h2>
          </div>
          <div className="px-6 pt-6 pb-12">
            <video id="videoPlayer" className="w-full border rounded-lg shadow-lg" controls src="https://www.w3schools.com/html/mov_bbb.mp4"></video>

            {/* Added Content Below the Video */}
            <div className="mt-6 text-white container max-w-[45rem] mx-auto">
              <div className="custom-theme">
                <div className="fr-view">
                  <h2 className="text-2xl font-bold mt-4">
                    <strong>Welcome to Your AI Tool Certificate Course by The Rundown!</strong>
                  </h2>
                  <p className="mt-2">
                    Before we get started with the AI Tools for Work course, don't forget to access all the free bonus resources that come with the purchase of the Kit by downloading them at the bottom of this lesson.
                  </p>
                  <p className="mt-2">You'll find our:</p>
                  <ul className="list-disc pl-5 mt-2">
                    <li>Expert ChatGPT Prompting Guide</li>
                    <li>Beginner Midjourney Guide</li>
                    <li>Essential AI Terminology Cheatsheets</li>
                    <li>Responsible use of AI checklist</li>
                    <li>AI Tools at Work Certification (available on completion of the course)</li>
                  </ul>
                  <p className="mt-2">
                    After you go ahead and download those resources, let's get right into it!
                  </p>
                  <hr className="my-4 border-white/20" />
                  <p className="mt-4">
                    If you’ve been feeling the buzz around AI and the fear of missing out (FOMO) that comes with it, you’re in the right place. Maybe you’ve dabbled with a few tools but are still asking yourself:
                  </p>
                  <p className="mt-2">
                    What exactly is AI? What does it do? And most importantly, how is it going to reshape your life—starting today and well into the future?
                  </p>
                  <h3 className="text-xl font-bold mt-4">Here’s the reality:</h3>
                  <p className="mt-2">
                    AI is the most transformative technology humans have ever created—and it’s only getting better.
                  </p>
                  <p className="mt-2">
                    Right now, we’re witnessing the <em>worst</em> version of AI we’ll ever see, which means the opportunity to get ahead is massive.
                  </p>
                  <p className="mt-2">
                    Avoiding AI? Not an option. Whether it’s in your job, your business, or your personal life, AI is about to be your new best friend—or your toughest competition.
                  </p>
                  <p className="mt-2">
                    By 2030, the business landscape will be divided into two camps:
                  </p>
                  
                  <img
                    src="https://files.cdn.thinkific.com/file_uploads/958811/images/f77/707/18c/Intro.png"
                    className="w-full mt-4"
                    alt="Course Intro"
                  />
                  <h3 className="text-xl font-bold mt-4"><strong>What This Course Offers</strong></h3>
                  <p className="mt-2">
                    This self-paced video course is for anyone who is new to the world of AI and recognizes the value that AI can bring to helping them work smarter and save precious time that can be used for an improved quality of life.
                  </p>
                  <p className="mt-2">
                    Once you have a solid foundational understanding of AI, even though the field moves so fast, you'll be able to easily adapt as these skills will serve you well both now and into the future.
                  </p>
                  <p className="mt-2">
                    <strong>Ready to level up your AI game? Let's get started!</strong>
                  </p>
                  <hr className="my-4 border-white/20" />
                  <p className="mt-4">
                    <strong>Need help?</strong> We're here for you! Please email us at <a href="mailto:support@therundown.ai" className="text-blue-500 hover:underline">support@therundown.ai</a> if you have any questions or feedback along the way. Your success is our priority!
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="p-4 bg-background-back2  sticky bottom-0 w-full z-[9] ">
            <div className="flex gap-4 justify-center">
              <button className="border-2 border-customBlue px-4 py-2 rounded text-white hover:bg-background-primary  transition text-[12px] font-bold uppercase ">
                Mark Incomplete
              </button>
              <button className="border-2 flex items-center gap-1 border-customBlue px-4 py-2 rounded text-white hover:bg-background-btn bg-background-primary transition text-[12px] font-bold uppercase ">
                Continue <FaArrowRightLong />
              </button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}